/**
 * 渠道与交易接口的类型与调用:与 apps/api 的 /v1/channel、/v1/attribution、/v1/platform(渠道与结算)、
 * /v1/admin/commerce、/v1/shop、/v1/pay、/v1/promo 一一对应。金额一律「分」,比例用万分比,页面负责换成元和百分比。
 * 枚举标签在 ui/commerce-labels.ts(只被交易页面引用,不进首屏主包)。
 */
import type { ApiClient } from './client.js';

export const SOURCE_LABELS: Record<string, string> = {
  link: '推广链接',
  activation: '激活码',
  import: '导入 / 入班',
  school: '学校',
  transfer: '转移',
};
export const PAY_METHOD_LABELS: Record<string, string> = {
  wechat: '微信支付',
  alipay: '支付宝',
  bank_transfer: '对公转账',
  offline_registered: '线下收款登记',
};
/** 二级开通申请的状态(复用渠道状态的键,但申请上「active」读作已开通) */
export const APPLICATION_LABELS: Record<string, string> = {
  pending_review: '待平台备案',
  active: '已开通',
  rejected: '已驳回',
};
export const RATE_LABELS: Record<string, string> = {
  course_pack: '课程包',
  exam_fee: '考试报名',
  module: '模块采购',
  l2_rebate: '二级返',
};
/** 页面上可定的比例项(模块采购单没有学员归属、永远不分成,不再给填) */
export const RATE_KINDS = ['course_pack', 'exam_fee', 'l2_rebate'] as const;
/** 状态 → 标签颜色 */
export const ORDER_TONE: Record<string, string> = {
  pending_payment: 'warn',
  pending_confirm: 'warn',
  paid: 'ok',
  partial_refunded: 'brand',
  refunded: '',
  closed: '',
};
export const STATEMENT_TONE: Record<string, string> = {
  pending_platform: 'amber',
  pending_confirm: 'warn',
  disputed: 'bad',
  confirmed: 'brand',
  confirmed_default: 'brand',
  pending_payout: 'brand',
  paid: 'ok',
};
export const ENTRY_TONE: Record<string, string> = {
  pending: 'warn',
  on_hold: 'bad',
  in_statement: 'brand',
  settled: 'ok',
  void: '',
};

/** 分 → 「1,234.56」 */
export function yuan(cents: number | null | undefined): string {
  const c = cents ?? 0;
  const sign = c < 0 ? '−' : '';
  const v = Math.abs(c) / 100;
  return sign + v.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** 万分比 → 「30%」「5.5%」 */
export const pct = (bp: number | null | undefined) =>
  bp === null || bp === undefined ? '—' : `${bp / 100}%`;
/** 元(输入框)→ 分;非法返回 null */
export function toCents(v: string | number): number | null {
  const s = String(v).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [i, f = ''] = s.split('.');
  return Number(i) * 100 + Number(f.padEnd(2, '0'));
}

export interface RateMap {
  course_pack?: number;
  exam_fee?: number;
  module?: number;
  l2_rebate?: number;
}
export interface Money {
  pendingCents: number;
  inStatementCents: number;
  settledCents: number;
  totalCents: number;
}
export interface Statement {
  id: string;
  tenantId: string;
  tenantName?: string;
  number: string;
  period: string;
  grossCents: number;
  offsetCents: number;
  payableCents: number;
  lineCount: number;
  status: string;
  /** 平台财务「确认并推送」的时间;待平台确认时为空 */
  platformConfirmedAt: string | null;
  /** 机构确认截止(推送起 N 天);待平台确认时为空 */
  dueAt: string | null;
  orgConfirmedAt: string | null;
  invoice: {
    no: string;
    fileKey: string | null;
    uploadedAt: string;
    verifiedAt?: string;
    /** 平台驳回(不合规,重开重交) */
    rejectedAt?: string;
    rejectReason?: string;
    history?: { no: string; uploadedAt: string; rejectedAt: string; reason: string }[];
  } | null;
  payout: { voucherNo: string; amountCents: number; paidAt: string } | null;
  createdAt: string;
}
export interface SubChannel {
  tenantId: string;
  name: string;
  filingNo: string;
  status: string;
  since: string;
  rates: RateMap;
  members: number;
  money: Money;
}
export interface ChannelOverview {
  channel: { level: number; filingNo: string; status: string; parentName: string; since: string };
  rates: RateMap;
  money: Money;
  members: number;
  funnel: { visits: number; registered: number; effective: number; paid: number };
  links: number;
  statements: Statement[];
  subs: SubChannel[];
}
export interface PromoLink {
  id: string;
  code: string;
  name: string;
  landing: string;
  target: string | null;
  status: string;
  expiresAt: string | null;
  visits: number;
  registered: number;
  effective: number;
  paid: number;
  createdAt: string;
}
export interface Member {
  id: string;
  name: string;
  nickname: string;
  phone: string;
  tenantId: string | null;
  source: string | null;
  ref: string | null;
  boundAt: string | null;
  consented: boolean;
  effective: boolean;
  paidCents: number;
}
export interface Trace {
  student: { id: string; name: string; nickname: string };
  events: {
    at: string;
    kind: string;
    source: string | null;
    ref: string | null;
    note: string | null;
    to: string;
    from: string;
  }[];
  orders: {
    number: string;
    paidAt: string | null;
    amountCents: number;
    refundedCents: number;
    name: string;
    attribution: string;
  }[];
}
export interface Transfer {
  id: string;
  number: string | null;
  studentId: string;
  student: string;
  fromTenantId: string | null;
  toTenantId: string;
  fromName: string;
  toName: string;
  /** requested 申请转移 · auto 自动转移(B 有真实服务且 A 满 N 天无服务)· merge · arbitration · reassign 平台重新指定 */
  kind: string;
  /** 谁发起的:org 机构(缺省)/ family 家长或本人 / platform 平台重新指定 */
  initiator?: string | null;
  status: string;
  reason: string;
  /** 自动转移的依据 */
  basis: string | null;
  fromConfirmedAt: string | null;
  toConfirmedAt: string | null;
  /** 申请:确认截止;自动转移:申诉截止 */
  expiresAt: string;
  effectiveAt: string | null;
  closedReason: string | null;
  appealReason: string | null;
  appealedAt: string | null;
  decisionNote: string | null;
  decidedAt: string | null;
  waitingForMe: boolean;
  /** 被自动转走的一方,申诉期内可申诉 */
  canAppeal: boolean;
  createdAt: string;
}
/** 平台「归属申诉」列表行 */
export interface AppealRow extends Omit<Transfer, 'waitingForMe' | 'canAppeal'> {
  studentName: string;
}
/** 仲裁依据:一方的服务日志 */
export interface ServiceSide {
  tenantId: string | null;
  name: string;
  lastServiceAt: string | null;
  records: { kind: string; at: string; label: string }[];
}
export interface AppealEvidence {
  transfer: AppealRow;
  student: { id: string; name: string; nickname: string };
  events: Trace['events'];
  orders: Trace['orders'];
  from: ServiceSide;
  to: ServiceSide;
  /** 平台规则「归属自动转移 · 无服务天数」 */
  days: number;
}
export interface Entry {
  id: string;
  tenantId: string;
  orderId: string;
  orderNumber: string;
  orderType: string;
  studentId: string | null;
  kind: string;
  role: string;
  baseCents: number;
  rateBp: number;
  amountCents: number;
  status: string;
  refundId: string | null;
  statementId: string | null;
  note: string | null;
  occurredAt: string;
}
export interface Dispute {
  id: string;
  tenantId: string;
  tenantName?: string;
  statementId: string;
  entryId: string;
  claim: string;
  claimedCents: number | null;
  status: string;
  resolution: string | null;
  adjustmentCents: number | null;
  resolvedAt: string | null;
  createdAt: string;
  entry?: Entry | null;
}
export interface StatementDetail {
  statement: Statement;
  entries: Entry[];
  disputes: Dispute[];
}
export interface Application {
  id: string;
  tenantId: string;
  applicant: {
    name: string;
    code: string;
    adminPhone: string;
    adminName: string;
    rates: RateMap;
    note?: string;
  };
  qualification: string[];
  status: string;
  createdTenantId: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}
export interface PlatformChannel {
  id: string;
  tenantId: string;
  level: number;
  parentTenantId: string | null;
  /** 一级停用后改平台直管的二级:原上级与转直管时间 */
  formerParentTenantId: string | null;
  directSince: string | null;
  parentName: string;
  formerParentName: string;
  filingNo: string;
  status: string;
  suspendReason: string | null;
  suspendedAt: string | null;
  name: string;
  code: string;
  expiresAt: string | null;
  rates: RateMap;
  /** 待结算明细合计(< 0 = 结转中;停用的算欠款) */
  balanceCents: number;
  debt: boolean;
  /** 近 N 天新增归属与有效率;suspect = 疑似异常(只是提示) */
  growth: { recentNew: number; recentEffective: number; rate: number | null; suspect: boolean } | null;
  createdAt: string;
}
/** 渠道异常增长(统计页 / 合作机构列表的「疑似异常」) */
export interface ChannelGrowth {
  tenantId: string;
  recentNew: number;
  recentEffective: number;
  rate: number | null;
  suspect: boolean;
  rule: { days: number; minNew: number; maxEffectivePct: number };
}
/** 暂缓复核单 */
export interface HoldRow {
  id: string;
  tenantId: string;
  tenantName: string;
  scope: 'range' | 'students';
  rangeFrom: string | null;
  rangeTo: string | null;
  reason: string;
  status: 'open' | 'released' | 'voided';
  memberCount: number;
  heldEntries: number;
  heldCents: number;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}
export interface HoldDetail {
  hold: HoldRow;
  members: {
    id: string;
    name: string;
    nickname: string;
    boundAt: string | null;
    consented: boolean;
    effective: boolean;
  }[];
  entries: Entry[];
}
/** 平台直管学员(一级停用后转来的) */
export interface DirectStudent {
  id: string;
  name: string;
  nickname: string;
  since: string | null;
  formerName: string;
  basis: string | null;
  lastService: { tenantId: string; name: string; label: string; at: string } | null;
}
/** 应结为负(结转中)的合作机构 */
export interface DebtRow {
  tenantId: string;
  tenantName: string;
  status: string;
  balanceCents: number;
  debtCents: number;
  suspended: boolean;
}
/** 资金异常台账 */
export interface FundIssue {
  id: string;
  tenantId: string;
  kind: string;
  status: string;
  orderId: string | null;
  orderNumber: string | null;
  refundId: string | null;
  refundNumber: string | null;
  channelTxn: string | null;
  expectedCents: number | null;
  actualCents: number | null;
  detail: string | null;
  note: string | null;
  resolvedAt: string | null;
  createdAt: string;
}
/** 机构收入结算(机构自卖、线上支付进平台收款号的钱)一行:卖方 × 月 */
export interface SellerLedgerRow {
  tenantId: string;
  tenantName: string;
  period: string;
  orders: number;
  grossCents: number;
  refundCents: number;
  payableCents: number;
  status: 'pending' | 'settled';
  voucherNo: string | null;
  settledAt: string | null;
  note: string | null;
}
/** 渠道看的「待校方确认」订单 */
export interface SchoolPendingOrder {
  id: string;
  number: string;
  name: string;
  amountCents: number;
  refundedCents: number;
  paidAt: string | null;
  decideBy: string | null;
  school: string;
  student: string;
}
/** 家长端「服务机构」 */
export interface FamilyAttribution {
  student: { id: string; name: string; nickname: string };
  current: { tenantId: string; name: string } | null;
  candidates: { tenantId: string; name: string }[];
  days: number;
  transfers: {
    id: string;
    kind: string;
    initiator: string;
    status: string;
    fromName: string;
    toName: string;
    reason: string;
    fromConfirmed: boolean;
    toConfirmed: boolean;
    expiresAt: string;
    effectiveAt: string | null;
    number: string | null;
    closedReason: string | null;
    canWithdraw: boolean;
    createdAt: string;
  }[];
}
/** 退费审核通过前的重算 */
export interface RefundRequote {
  refundId: string;
  requestedCents: number;
  amountCents: number;
  diffCents: number;
  consumedHours: number;
  lessonHours: number;
}
export interface Pack {
  id: string;
  tenantId: string;
  name: string;
  courseIds: string[];
  courses: { id: string; name: string }[];
  lessonHours: number;
  months: number;
  priceCents: number;
  status: string;
  owned?: boolean;
}
export interface PackInput {
  name: string;
  courseIds: string[];
  lessonHours: number;
  months: number;
  priceCents: number;
  status?: 'on_sale' | 'off';
}
export interface OrderView {
  id: string;
  number: string;
  type: string;
  item: {
    name: string;
    packId?: string;
    courseIds?: string[];
    lessonHours?: number;
    months?: number;
    /** 认证考报名费(type = exam_fee) */
    exam?: {
      examId: string;
      registrationId: string;
      examName: string;
      group: string;
      groupLabel: string;
      level: string | null;
    };
    /** 算力包(type = ai_pack):充进谁的钱包、多少 token */
    ai?: { packId: string; tokens: number; wallet: 'student' | 'tenant'; ownerId: string };
    /** 用了优惠券的课程包订单:标价与让利(amountCents 已经是减完的实付) */
    coupon?: { grantId: string; couponId: string; name: string; discountCents: number; listCents: number };
  };
  amountCents: number;
  refundedCents: number;
  status: string;
  payMethod: string | null;
  paidAt: string | null;
  expiresAt: string | null;
  closedAt: string | null;
  createdAt: string;
  studentId: string | null;
  studentName: string;
  offline: {
    registeredAt: string;
    voucher: string | null;
    confirmedAt?: string;
    /** 没生效就关了:家长说没付过 / 机构撤回 / 7 天没人确认 */
    closed?: { reason: 'denied' | 'revoked' | 'expired'; at: string; by: string | null; note: string | null };
  } | null;
}
export interface SellerOrder extends OrderView {
  payerPhone: string;
  attributed: boolean;
  commission: string;
}
import type { PayInfo } from './pay.js';
export type { PayInfo };
export interface RefundLite {
  id: string;
  number: string;
  amountCents: number;
  /** 申请时的金额(审核时按已消耗课时重算变少了才和 amountCents 不同) */
  requestedCents?: number | null;
  status: string;
  /** 通道状态:PROCESSING = 通道受理了钱在路上 */
  channelState?: string | null;
  reason: string;
  reviewNote: string | null;
  createdAt: string;
  refundedAt: string | null;
}
export interface MyOrder {
  order: OrderView;
  pay: PayInfo | null;
  /** 线下收款:我(监护人或本人)能确认已付 / 说没付过 */
  canConfirm?: boolean;
  refunds: RefundLite[];
}
export interface SellerRefund extends RefundLite {
  orderId: string;
  refundableCents: number;
  rule: string;
  order: {
    id: string;
    number: string;
    name: string;
    type?: string;
    amountCents: number;
    refundedCents: number;
    paidAt: string | null;
    status: string;
  } | null;
  studentName: string;
}
export interface RefundQuote {
  refundableCents: number;
  consumedHours: number;
  rule: 'auto' | 'manual';
  /** 认证考报名费:可退截止(开考前 N 小时)与说明 */
  until?: string | null;
  note?: string;
}
export interface PromoVisit {
  usable: boolean;
  landing: string;
  target: string | null;
  code: string;
  orgName: string;
  siteCode: string;
  /** 落地页埋点用:哪个渠道的哪条链接 */
  linkId: string;
  channelId: string;
}
export interface Paged<T> {
  total: number;
  page: number;
  size: number;
  items: T[];
}

export function commerceApi(api: ApiClient) {
  return {
    channel: {
      overview: () => api.get<ChannelOverview>('/v1/channel/overview'),
      rates: () =>
        api.get<{ current: RateMap; history: { kind: string; rateBp: number; effectiveFrom: string }[] }>(
          '/v1/channel/rates',
        ),
      links: () => api.get<PromoLink[]>('/v1/channel/links'),
      createLink: (body: { name: string; landing: string; target?: string; expiresAt?: string }) =>
        api.post<PromoLink>('/v1/channel/links', body),
      linkStatus: (id: string, status: 'active' | 'disabled') =>
        api.post(`/v1/channel/links/${id}/status`, { status }),
      members: (q: { page?: number; size?: number; q?: string }) =>
        api.get<Paged<Member>>('/v1/channel/members', { query: q }),
      trace: (studentId: string) => api.get<Trace>(`/v1/channel/members/${studentId}/trace`),
      entries: (status?: string) => api.get<Entry[]>('/v1/channel/entries', { query: { status } }),
      statements: () => api.get<Statement[]>('/v1/channel/statements'),
      statement: (id: string) => api.get<StatementDetail>(`/v1/channel/statements/${id}`),
      confirm: (id: string) => api.post<Statement>(`/v1/channel/statements/${id}/confirm`),
      dispute: (id: string, body: { entryId: string; claim: string; claimedCents?: number }) =>
        api.post<StatementDetail>(`/v1/channel/statements/${id}/disputes`, body),
      invoice: (id: string, body: { no: string; fileKey?: string }) =>
        api.post<Statement>(`/v1/channel/statements/${id}/invoice`, body),
      subs: () => api.get<SubChannel[]>('/v1/channel/subs'),
      /** 待校方确认分成的订单(30 天不确认按拒绝) */
      schoolPending: () => api.get<SchoolPendingOrder[]>('/v1/channel/school-pending'),
      applications: () => api.get<Application[]>('/v1/channel/applications'),
      apply: (body: {
        name: string;
        code: string;
        adminPhone: string;
        adminName: string;
        rates: { course_pack?: number; exam_fee?: number };
        note?: string;
        qualification: string[];
      }) => api.post<Application>('/v1/channel/applications', body),
    },
    attribution: {
      transfers: () => api.get<Transfer[]>('/v1/attribution/transfers'),
      request: (body: { studentId: string; toCode?: string; reason: string }) =>
        api.post<{ bound: boolean; transfer?: Transfer }>('/v1/attribution/transfers', body),
      confirm: (id: string) => api.post<Transfer>(`/v1/attribution/transfers/${id}/confirm`),
      reject: (id: string, note: string) => api.post(`/v1/attribution/transfers/${id}/reject`, { note }),
      /** 对自动转移申诉(被转走的一方,7 天内) */
      appeal: (id: string, reason: string) =>
        api.post<Transfer>(`/v1/attribution/transfers/${id}/appeal`, { reason }),
      /** 家长 / 本人:孩子的服务机构与更换申请 */
      family: (studentId: string) =>
        api.get<FamilyAttribution>('/v1/attribution/family', { query: { studentId } }),
      familyRequest: (body: { studentId: string; toTenantId?: string; toCode?: string; reason: string }) =>
        api.post<FamilyAttribution['transfers'][number]>('/v1/attribution/family/transfers', body),
      familyWithdraw: (id: string) => api.post(`/v1/attribution/family/transfers/${id}/withdraw`, {}),
    },
    platform: {
      channels: () => api.get<PlatformChannel[]>('/v1/platform/channels'),
      /** 一步开通一级合作机构:站点 + 负责人账号 + 备案号 + 比例 */
      open: (body: {
        code: string;
        name: string;
        adminPhone: string;
        adminName: string;
        expiresAt?: string | null;
        rates: RateMap;
      }) =>
        api.post<{
          channel: PlatformChannel;
          tenant: { id: string; code: string; name: string };
          admin: { id: string; created: boolean };
        }>('/v1/platform/channels/open', body),
      register: (tenantCode: string, rates: RateMap) =>
        api.post<PlatformChannel>('/v1/platform/channels', { tenantCode, rates }),
      setRates: (tenantId: string, rates: RateMap) =>
        api.put<RateMap>(`/v1/platform/channels/${tenantId}/rates`, rates),
      suspend: (tenantId: string, suspended: boolean, reason?: string) =>
        api.post<{ subs: number; students: number }>(`/v1/platform/channels/${tenantId}/suspend`, {
          suspended,
          ...(reason ? { reason } : {}),
        }),
      members: (tenantId: string, q: { page?: number; q?: string }) =>
        api.get<Paged<Member>>(`/v1/platform/channels/${tenantId}/members`, { query: q }),
      anomalies: () => api.get<ChannelGrowth[]>('/v1/platform/channels/anomalies'),
      /** 标记异常增长 → 这些学员的分成暂缓 */
      markHold: (body: {
        tenantId: string;
        studentIds?: string[];
        from?: string;
        to?: string;
        reason: string;
      }) => api.post<HoldRow>('/v1/platform/finance/holds', body),
      holds: (scope: 'open' | 'all' = 'open') =>
        api.get<HoldRow[]>('/v1/platform/finance/holds', { query: { scope } }),
      hold: (id: string) => api.get<HoldDetail>(`/v1/platform/finance/holds/${id}`),
      decideHold: (id: string, body: { approve: boolean; note?: string }) =>
        api.post<HoldRow>(`/v1/platform/finance/holds/${id}/decide`, body),
      transfers: () => api.get<Transfer[]>('/v1/platform/attribution/transfers'),
      direct: (q: { page?: number; q?: string }) =>
        api.get<Paged<DirectStudent>>('/v1/platform/attribution/direct', { query: q }),
      reassign: (body: { studentIds: string[]; toTenantId: string; reason: string }) =>
        api.post<{ reassigned: number; skipped: { studentId: string; reason: string }[] }>(
          '/v1/platform/attribution/direct/reassign',
          body,
        ),
      publish: (id: string) => api.post<Statement>(`/v1/platform/finance/statements/${id}/publish`, {}),
      rejectInvoice: (id: string, reason: string) =>
        api.post<Statement>(`/v1/platform/finance/statements/${id}/reject-invoice`, { reason }),
      debts: () => api.get<DebtRow[]>('/v1/platform/finance/debts'),
      recover: (tenantId: string, body: { amountCents: number; voucher: string; note?: string }) =>
        api.post<{ leftCents: number }>(`/v1/platform/finance/debts/${tenantId}/recover`, body),
      fundIssues: (scope: 'open' | 'all' = 'open') =>
        api.get<FundIssue[]>('/v1/platform/finance/fund-issues', { query: { scope } }),
      resolveFundIssue: (id: string, note: string) =>
        api.post(`/v1/platform/finance/fund-issues/${id}/resolve`, { note }),
      sellerLedger: (period: string) =>
        api.get<SellerLedgerRow[]>('/v1/platform/finance/seller-ledger', { query: { period } }),
      settleSeller: (body: { tenantId: string; period: string; voucherNo: string; note?: string }) =>
        api.post<SellerLedgerRow>('/v1/platform/finance/seller-ledger/settle', body),
      applications: (status?: string) =>
        api.get<Application[]>('/v1/platform/channel-applications', { query: { status } }),
      approve: (id: string, rates?: RateMap) =>
        api.post<PlatformChannel>(`/v1/platform/channel-applications/${id}/approve`, rates ? { rates } : {}),
      reject: (id: string, note: string) =>
        api.post(`/v1/platform/channel-applications/${id}/reject`, { note }),
      /** 分页;total 与 summary 按整个筛选结果算(不只当前页);q = 机构名称 / 站点码 / 单号的一段 */
      statements: (q: {
        period?: string | undefined;
        status?: string | undefined;
        q?: string | undefined;
        page?: number;
        pageSize?: number;
      }) =>
        api.get<{
          items: Statement[];
          total: number;
          page: number;
          pageSize: number;
          summary: {
            total: number;
            payableCents: number;
            toPublish: number;
            toPayCents: number;
            toVerify: number;
          };
          /** 按名称找时对得上的机构超过上限(500 家),只按其中一部分找了 */
          nameTruncated?: boolean;
        }>('/v1/platform/finance/statements', { query: q }),
      generate: (period: string) =>
        api.post<{ period: string; created: Statement[]; skipped: { tenantId: string; reason: string }[] }>(
          '/v1/platform/finance/statements/generate',
          { period },
        ),
      statement: (id: string) => api.get<StatementDetail>(`/v1/platform/finance/statements/${id}`),
      verifyInvoice: (id: string) =>
        api.post<Statement>(`/v1/platform/finance/statements/${id}/verify-invoice`),
      payout: (id: string, voucherNo: string) =>
        api.post<Statement>(`/v1/platform/finance/statements/${id}/payout`, { voucherNo }),
      /** 归属申诉:默认只看申诉中,all = 全部自动转移 */
      appeals: (scope: 'appealing' | 'all' = 'appealing') =>
        api.get<AppealRow[]>('/v1/platform/attribution/appeals', { query: { scope } }),
      evidence: (id: string) => api.get<AppealEvidence>(`/v1/platform/attribution/appeals/${id}`),
      decide: (id: string, body: { decision: 'uphold' | 'revoke'; note: string }) =>
        api.post<AppealRow>(`/v1/platform/attribution/appeals/${id}/decide`, body),
      disputes: () => api.get<Dispute[]>('/v1/platform/finance/disputes'),
      resolve: (id: string, body: { upheld: boolean; adjustmentCents?: number; resolution: string }) =>
        api.post<Dispute>(`/v1/platform/finance/disputes/${id}/resolve`, body),
    },
    seller: {
      /** 可放进课程包的课程(平台标准库 + 本机构自有;只列已上线的) */
      courses: () =>
        api.get<{ id: string; name: string; status: string; track: string; origin: string }[]>(
          '/v1/content/courses',
        ),
      packs: () => api.get<Pack[]>('/v1/admin/commerce/packs'),
      createPack: (body: PackInput) => api.post<Pack>('/v1/admin/commerce/packs', body),
      updatePack: (id: string, patch: Partial<PackInput>) =>
        api.patch<Pack>(`/v1/admin/commerce/packs/${id}`, patch),
      orders: (q: { type?: string; status?: string; page?: number; size?: number }) =>
        api.get<Paged<SellerOrder>>('/v1/admin/commerce/orders', { query: q }),
      offline: (body: {
        studentId: string;
        packId: string;
        method: 'bank_transfer' | 'offline_registered';
        voucher?: string;
      }) => api.post<OrderView>('/v1/admin/commerce/offline-orders', body),
      refunds: (status?: string) =>
        api.get<SellerRefund[]>('/v1/admin/commerce/refunds', { query: { status } }),
      /** 通过前按现在的已消耗课时重算 */
      requote: (id: string) => api.get<RefundRequote>(`/v1/admin/commerce/refunds/${id}/requote`),
      /** 通过(按重算后的金额退);返回退费单:refunded 已到账 / refunding 通道处理中 */
      approve: (id: string, note?: string) =>
        api.post<RefundLite>(`/v1/admin/commerce/refunds/${id}/approve`, note ? { note } : {}),
      reject: (id: string, note: string) => api.post(`/v1/admin/commerce/refunds/${id}/reject`, { note }),
      /** 家长确认前撤回线下收款登记 */
      revokeOffline: (id: string, reason: string) =>
        api.post<OrderView>(`/v1/admin/commerce/offline-orders/${id}/revoke`, { reason }),
      /** 本机构线上收入的月台账与平台结算状态 */
      settlements: () => api.get<SellerLedgerRow[]>('/v1/admin/commerce/settlements'),
    },
    shop: {
      packs: (studentId?: string) => api.get<Pack[]>('/v1/shop/packs', { query: { studentId } }),
      order: (body: { packId: string; studentId: string; method?: 'wechat' | 'alipay'; couponId?: string }) =>
        api.post<{ order: OrderView; pay: PayInfo | null }>('/v1/shop/orders', body),
      mine: () => api.get<MyOrder[]>('/v1/shop/orders'),
      cancel: (id: string) => api.post(`/v1/shop/orders/${id}/cancel`),
      confirmOffline: (id: string) => api.post<OrderView>(`/v1/shop/orders/${id}/confirm-offline`),
      /** 没付过这笔线下收款 → 订单关闭,通知机构 */
      denyOffline: (id: string, note?: string) =>
        api.post<OrderView>(`/v1/shop/orders/${id}/deny-offline`, note ? { note } : {}),
      quote: (id: string) => api.get<RefundQuote>(`/v1/shop/orders/${id}/refund-quote`),
      refund: (id: string, body: { amountCents?: number; reason: string }) =>
        api.post<RefundLite>(`/v1/shop/orders/${id}/refunds`, body),
    },
    promo: {
      visit: (code: string) => api.get<PromoVisit>(`/v1/promo/${encodeURIComponent(code)}`),
      seen: (code: string) => api.post<{ kept: boolean }>(`/v1/promo/${encodeURIComponent(code)}/seen`),
    },
  };
}
export type CommerceApi = ReturnType<typeof commerceApi>;
